/**
 * Santé de la plateforme (super admin) — W7, SU-8, DES-11, SR3-5.
 *
 * From GET /api/admin/platform/health (read only), refreshed every minute:
 *   - « À surveiller » first (DES-11): what is past a threshold, most severe
 *     first, each with where to look; « Rien à signaler » otherwise;
 *   - then the figures: Swoft send queue, calls and failures per carrier,
 *     qualification workflow, webhook deliveries (« Non disponible » until
 *     SU-2's collection exists), live streams of the instance, client errors.
 * A section the server could not read shows « Indisponible », never 0.
 * The audit log export stays in « Journal d'audit » (SR3-5): this page only
 * links to it.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, OctagonAlert, RefreshCw } from 'lucide-react';
import { getPlatformHealth, type HealthException, type PlatformHealth } from '@/api/endpoints/platformHealth';
import { Button } from '@/components/ui/button';
import { VFStatCard } from '@/components/vf/StatCard';
import { ErrorLine, PlatformPageHeader } from '@/components/backoffice/platform/PlatformBits';
import { formatRate } from '@/lib/metrics';
import { currentLocale } from '@/i18n/locale';

const REFRESH_MS = 60_000;

function Section({ id, title, children, footer }: { id: string; title: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <section aria-labelledby={id} className="space-y-3 rounded-vf border p-4" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-bg)' }}>
      <h2 id={id} className="text-base font-semibold">{title}</h2>
      {children}
      {footer && <div className="text-xs" style={{ color: 'var(--vf-text-2)' }}>{footer}</div>}
    </section>
  );
}

function ExceptionRow({ e }: { e: HealthException }) {
  const { t } = useTranslation(['admin']);
  const critical = e.severity === 'critical';
  const Icon = critical ? OctagonAlert : AlertTriangle;
  const values = e.kind === 'carrier_failures'
    ? { ...e.values, carrier: t(`admin:platformHealth.carriers.${e.values.carrier}`) }
    : e.kind === 'section_unavailable' ? { ...e.values, section: t(`admin:platformHealth.sections.${e.values.section}`) } : e.values;
  return (
    <li className="flex flex-wrap items-start gap-2 py-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden style={{ color: critical ? 'var(--vf-fill-danger)' : 'var(--vf-fill-warning, var(--vf-text-2))' }} />
      <span className="sr-only">{t(`admin:platformHealth.severity.${e.severity}`)} :</span>
      <span className="min-w-0 flex-1 text-sm">{t(`admin:platformHealth.exceptions.${e.kind}`, values)}</span>
      {e.link && <Link to={e.link} className="inline-flex min-h-11 items-center text-sm underline" style={{ color: 'var(--vf-accent-text)' }}>{t('admin:platformHealth.look')}</Link>}
    </li>
  );
}

export default function AdminPlatformHealth() {
  const { t } = useTranslation(['admin', 'common']);
  const health = useQuery<PlatformHealth>({
    queryKey: ['platform-health'],
    queryFn: ({ signal }) => getPlatformHealth(signal),
    refetchInterval: REFRESH_MS,
    retry: false,
  });
  const h = health.data;
  const st = (available: boolean | undefined) => (health.isError || available === false ? 'error' : h ? 'ready' : 'loading') as 'error' | 'ready' | 'loading';
  const n = (v: number) => v.toLocaleString(currentLocale());
  const ago = (iso: string | null) => (iso ? t('admin:platformHealth.outbox.oldestAgo', { minutes: Math.max(0, Math.round((Date.parse(h!.generated_at) - Date.parse(iso)) / 60_000)) }) : t('admin:platformHealth.outbox.none'));

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-4 sm:p-6">
      <PlatformPageHeader
        title={t('admin:superAdminLayout.nav.platformHealth')}
        subtitle={h ? t('admin:platformHealth.subtitle', { time: new Date(h.generated_at).toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit' }) }) : t('admin:platformHealth.subtitleLoading')}
        actions={(
          <>
            <Button asChild variant="outline"><Link to="/AdminAuditLogs">{t('admin:platformHealth.auditExport')}</Link></Button>
            <Button type="button" variant="outline" className="gap-2" onClick={() => void health.refetch()} disabled={health.isFetching}>
              <RefreshCw className={`h-4 w-4 ${health.isFetching ? 'animate-spin motion-reduce:animate-none' : ''}`} aria-hidden />
              {t('common:actions.refresh')}
            </Button>
          </>
        )}
      />

      {health.isError && <ErrorLine message={t('admin:platformHealth.loadFailed')} error={health.error} onRetry={() => void health.refetch()} />}

      <Section id="health-exceptions" title={t('admin:platformHealth.watchTitle')}>
        {!h && !health.isError && <p className="text-sm" role="status" style={{ color: 'var(--vf-text-2)' }}>{t('common:actions.loading')}</p>}
        {/* REV-W7-F8: a failed refresh keeps the last data in the cache; the
            list would show stale exceptions (or a stale « all clear »). */}
        {h && !health.isError && h.exceptions.length === 0 && <p className="text-sm" style={{ color: 'var(--vf-text-2)' }}>{t('admin:platformHealth.allClear', { hours: h.period_hours })}</p>}
        {h && !health.isError && h.exceptions.length > 0 && <ul className="divide-y" style={{ borderColor: 'var(--vf-border)' }}>{h.exceptions.map((e) => <ExceptionRow key={e.id} e={e} />)}</ul>}
      </Section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section id="health-outbox" title={t('admin:platformHealth.outbox.title')} footer={h?.outbox.available ? ago(h.outbox.oldest_pending_at) : undefined}>
          <div className="grid grid-cols-3 gap-3">
            <VFStatCard label={t('admin:platformHealth.outbox.pending')} state={st(h?.outbox.available)} value={h?.outbox.available ? n(h.outbox.pending) : undefined} />
            <VFStatCard label={t('admin:platformHealth.outbox.failed')} state={st(h?.outbox.available)} value={h?.outbox.available ? n(h.outbox.failed) : undefined} />
            <VFStatCard label={t('admin:platformHealth.outbox.dead')} state={st(h?.outbox.available)} value={h?.outbox.available ? n(h.outbox.dead) : undefined} />
          </div>
        </Section>

        <Section id="health-carriers" title={t('admin:platformHealth.carriersTitle', { hours: h?.period_hours ?? 24 })}>
          {st(h?.carriers.available) === 'error' && <p className="text-sm" style={{ color: 'var(--vf-text-2)' }}>{t('common:stat.unavailable')}</p>}
          {h?.carriers.available && (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <caption className="sr-only">{t('admin:platformHealth.carriersTitle', { hours: h.period_hours })}</caption>
                <thead style={{ color: 'var(--vf-text-3)' }}>
                  <tr>
                    <th scope="col" className="h-9 px-3 text-left text-[11px] font-semibold uppercase tracking-wide">{t('admin:platformHealth.columns.carrier')}</th>
                    <th scope="col" className="h-9 px-3 text-right text-[11px] font-semibold uppercase tracking-wide">{t('admin:platformHealth.columns.calls')}</th>
                    <th scope="col" className="h-9 px-3 text-right text-[11px] font-semibold uppercase tracking-wide">{t('admin:platformHealth.columns.failed')}</th>
                    <th scope="col" className="h-9 px-3 text-right text-[11px] font-semibold uppercase tracking-wide">{t('admin:platformHealth.columns.rate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {h.carriers.rows.map((r) => (
                    <tr key={r.carrier} className="border-t" style={{ borderColor: 'var(--vf-border)' }}>
                      <td className="h-10 px-3">{t(`admin:platformHealth.carriers.${r.carrier}`)}</td>
                      <td className="h-10 px-3 text-right tabular-nums">{n(r.calls)}</td>
                      <td className="h-10 px-3 text-right tabular-nums">{n(r.failed)}</td>
                      <td className="h-10 px-3 text-right tabular-nums">{formatRate(r.failure_rate, currentLocale())}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        <Section id="health-qualifications" title={t('admin:platformHealth.qualifications.title')}>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <VFStatCard label={t('admin:platformHealth.qualifications.pending')} state={st(h?.qualifications.available)} value={h?.qualifications.available ? n(h.qualifications.pending + h.qualifications.committing) : undefined} />
            <VFStatCard label={t('admin:platformHealth.qualifications.overdue')} state={st(h?.qualifications.available)} value={h?.qualifications.available ? n(h.qualifications.overdue) : undefined} hint={t('admin:platformHealth.qualifications.overdueHint', { minutes: h?.thresholds.qualificationOverdueMinutes ?? 5 })} />
            <VFStatCard label={t('admin:platformHealth.qualifications.failed')} state={st(h?.qualifications.available)} value={h?.qualifications.available ? n(h.qualifications.failed_24h) : undefined} />
            <VFStatCard label={t('admin:platformHealth.qualifications.eventsLost')} state={st(h?.qualifications.available)} value={h?.qualifications.available ? n(h.qualifications.events_lost_24h ?? 0) : undefined} hint={t('admin:platformHealth.qualifications.eventsLostHint')} />
          </div>
        </Section>

        <Section id="health-webhooks" title={t('admin:platformHealth.webhooks.title')}>
          {/* REV-W7-F7: webhooks ship (SU-2): `available: false` is a failed
              read, shown « Indisponible » like the other sections. */}
          <div className="grid grid-cols-3 gap-3">
            <VFStatCard label={t('admin:platformHealth.webhooks.pending')} state={st(h?.webhooks.available)} value={h?.webhooks.available ? n(h.webhooks.pending) : undefined} />
            <VFStatCard label={t('admin:platformHealth.webhooks.failed')} state={st(h?.webhooks.available)} value={h?.webhooks.available ? n(h.webhooks.failed) : undefined} />
            <VFStatCard label={t('admin:platformHealth.webhooks.delivered')} state={st(h?.webhooks.available)} value={h?.webhooks.available ? n(h.webhooks.delivered) : undefined} />
          </div>
        </Section>

        <Section id="health-sse" title={t('admin:platformHealth.sse.title')} footer={t('admin:platformHealth.sse.scope')}>
          <div className="grid grid-cols-2 gap-3">
            <VFStatCard label={t('admin:platformHealth.sse.dialer')} state={st(h?.sse.available)} value={h?.sse.available ? n(h.sse.dialer) : undefined} />
            <VFStatCard label={t('admin:platformHealth.sse.transcript')} state={st(h?.sse.available)} value={h?.sse.available ? n(h.sse.transcript) : undefined} />
          </div>
        </Section>

        <Section
          id="health-client-errors"
          title={t('admin:superAdminLayout.nav.clientErrors')}
          footer={<Link to="/AdminClientErrors?period=24h" className="inline-flex min-h-11 items-center underline" style={{ color: 'var(--vf-accent-text)' }}>{t('admin:platformHealth.clientErrorsLink')}</Link>}
        >
          <VFStatCard label={t('admin:platformHealth.clientErrors24h')} state={st(h?.client_errors.available)} value={h?.client_errors.available ? n(h.client_errors.last_24h) : undefined} />
        </Section>
      </div>
    </div>
  );
}
